/**
 * HostnameRouter — top-level guard that routes by hostname BEFORE any other route.
 *
 * Phase 1 — Identity Migration:
 *   identity.christyvonta.com/*  →  301 to  /identity/start  on the same SPA build.
 *
 * Future phases will use this surface to:
 *   - serve EcosystemPortal as `/` only on kissedbycvon.com,
 *   - serve a Coaching Door landing as `/` only on christyvonta.com,
 *   - serve a University Door landing as `/` only on kisseduniversity.com.
 *
 * Returns null when no redirect applies — the normal route tree renders.
 */
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DOORS, useDoorContext } from "@/hooks/useDoorContext";

export default function HostnameRouter() {
  const { doorKey } = useDoorContext();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  useEffect(() => {
    if (doorKey === DOORS.CONNECT) {
      // connect.christyvonta.com — Check In with Dr. C companion app.
      // Land visitors on /checkin so the mobile-first PWA route tree
      // renders. Never scoop KCIS super-admin login or the KCIS admin
      // console into this door — the Check-In admin SSO hop from
      // /checkin/admin → /admin/login?next=%2Fcheckin%2Fadmin MUST
      // land on the real KCIS sign-in page rather than the consumer
      // welcome.
      if (/^\/checkin(\/|$)/i.test(pathname)) return;
      if (/^\/(admin|kcis)(\/|$)/i.test(pathname)) return;
      navigate(`/checkin${search || ""}`, { replace: true });
      return;
    }
    if (doorKey !== DOORS.IDENTITY) return;
    // Consolidated home for the Identity Alignment Assessment is now
    // /coaching/identity/*. When identity.christyvonta.com serves this
    // SPA, land the visitor on the canonical route.
    if (/^\/coaching\/identity(\/|$)/i.test(pathname)) return;
    navigate(`/coaching/identity${search || ""}`, { replace: true });
  }, [doorKey, pathname, search, navigate]);

  return null;
}
